/**
 * Centralized timer manager for authentication-related timers
 * Prevents memory leaks and ensures proper cleanup
 */
export class AuthTimerManager {
  private timers: Map<string, NodeJS.Timeout> = new Map();
  private isDestroyed = false;

  /**
   * Set a timer with a unique identifier
   */
  setTimer(id: string, callback: () => void, delay: number): void {
    if (this.isDestroyed) {
      console.warn(`[AuthTimerManager] Cannot set timer ${id}, manager is destroyed`);
      return;
    }

    // Clear existing timer with same ID
    this.clearTimer(id);

    const timer = setTimeout(() => {
      if (!this.isDestroyed) {
        callback();
        this.timers.delete(id);
      }
    }, delay);

    this.timers.set(id, timer);
  }

  /**
   * Clear a specific timer by ID
   */
  clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }

  /**
   * Clear all timers
   */
  clearAllTimers(): void {
    for (const [id, timer] of this.timers) {
      clearTimeout(timer);
    }
    this.timers.clear();
  }

  /**
   * Check if a timer exists
   */
  hasTimer(id: string): boolean {
    return this.timers.has(id);
  }

  /**
   * Get the number of active timers
   */
  getTimerCount(): number {
    return this.timers.size;
  }

  /**
   * Destroy the timer manager and clear all timers
   * Prevents any future timer operations
   */
  destroy(): void {
    this.clearAllTimers();
    this.isDestroyed = true;
  }

  /**
   * Get all active timer IDs (for debugging)
   */
  getActiveTimerIds(): string[] {
    return Array.from(this.timers.keys());
  }
}

// Create a singleton instance for the entire app
export const authTimerManager = new AuthTimerManager();